import { i18n } from 'boot/i18n';
import { filterPropertyTypes } from 'boot/globals';
import { Timestamp } from 'firebase/firestore';
import { useTime } from 'src/composables/time';
import { DATE_FILTER_OPERATORS } from 'src/constants/filters';
import { formatDuration } from 'src/utils/DurationUtils';

import { getBooleanOptions, getNumericCriteriaTypes } from './criteriaTypes';
import { BETWEEN, CUSTOM_DATES, CUSTOM_RANGE } from './filterStatement';

/**
 * Builds the text shown on a filter's chip.
 *
 * Moved out of the dialog unchanged. The label is written onto the statement and persisted, so
 * filters saved before and after this have to read identically; anything cosmetic here is a
 * migration, not a tidy-up.
 *
 * The names behind selected client and user ids are looked up rather than stored, so they are
 * passed in: this module has no business reaching into stores.
 */

/** Beyond this many selections, listing them stops being readable and a count is shown instead. */
const MAX_LISTED_SELECTIONS = 5;

/*
 * Stands in for a value that is missing: a label is built after canApplyFilter has passed, so
 * mostly this means a stored selection whose record or option is gone. The dialog it replaces
 * rendered the string "null" on some of these paths and "?" on others.
 */
const MISSING = '?';

const or = () => ` ${i18n.t('common.or')} `;

/*
 * Three variants, and the difference is not decoration. filterPrefix undefined means the criterion
 * did not ask for one, so the criterion's own label is used with a colon. Explicit null means the
 * criterion wants no prefix at all. A string is used as given, and gains a colon only for the
 * types that do not already read as a sentence: "Age > 5" needs no colon, "Status: Active" does.
 */
function getLabelPrefix(criterion) {
  const { filterPrefix, filterType } = criterion ?? {};

  if (filterPrefix === undefined) {
    return `${criterion?.label}: `;
  }
  if (filterPrefix === null) {
    return '';
  }

  const readsAsSentence = [
    filterPropertyTypes.Number,
    filterPropertyTypes.Duration,
    filterPropertyTypes.Boolean,
  ].includes(filterType);

  return readsAsSentence ? filterPrefix : `${filterPrefix}: `;
}

function getShortOperator(filterCriteriaType) {
  return getNumericCriteriaTypes()
    .find((type) => type.value === filterCriteriaType)
    ?.shortLabel;
}

function toLocaleDate(value) {
  if (!(value instanceof Timestamp)) {
    return null;
  }

  const { getLocaleDateString } = useTime();

  return getLocaleDateString(value);
}

function joinNames(ids, records, nameKey) {
  const names = (ids ?? [])
    .map((id) => records?.find((record) => record.id === id)?.[nameKey])
    .filter(Boolean);

  return names.length > 0 ? names.join(or()) : MISSING;
}

/** Lists the selections while there are few enough to read, and counts them after that. */
function describeSelection(ids, records, nameKey) {
  const count = ids?.length ?? 0;

  return count < MAX_LISTED_SELECTIONS
    ? joinNames(ids, records, nameKey)
    : i18n.t('common.numSelectedItems', { count });
}

/*
 * The prefix already carries its own separator, or is empty. Appending a space to it regardless
 * wrote "Age:  = 5" with two, and " = 5" with a leading one, into a label that is then persisted.
 */
const withPrefix = (prefix, rest) => (prefix === '' ? rest : `${prefix.trimEnd()} ${rest}`);

function describeNumeric(statement, prefix, format) {
  const short = getShortOperator(statement.filterCriteriaType);

  if (statement.filterCriteriaType === BETWEEN) {
    const from = format(statement.filteredItems?.from);
    const to = format(statement.filteredItems?.to);
    return withPrefix(prefix, `${short} ${from} ${i18n.t('common.and')} ${to}`);
  }

  return withPrefix(prefix, `${short} ${format(statement.filteredItems)}`);
}

function describeDate(statement, periodPresetOptions) {
  if (statement.dateOperator === DATE_FILTER_OPERATORS.IS_MORE_THAN) {
    const weeks = statement.filteredItems;

    return Number.isInteger(weeks)
      ? i18n.t('common.filter.dateOperatorIsMoreThanChipLabel', weeks, { count: weeks })
      : MISSING;
  }

  const preset = periodPresetOptions?.find((option) => option.value === statement.filterCriteriaType);

  if (!preset?.isCustom) {
    return preset?.label ?? MISSING;
  }

  if (preset.value === CUSTOM_DATES) {
    const dates = [...(statement.filterDateItems ?? [])]
      .sort()
      .map(toLocaleDate)
      .filter(Boolean);

    return dates.length > 0 ? dates.join(or()) : MISSING;
  }

  if (preset.value === CUSTOM_RANGE) {
    const from = toLocaleDate(statement.fromDate) ?? MISSING;
    const to = toLocaleDate(statement.toDate) ?? MISSING;
    return `${from} ${i18n.t('common.to')} ${to}`;
  }

  return MISSING;
}

function describeOptions(statement, criterion) {
  const items = criterion?.options?.filterCriteriaItems ?? [];

  const labels = [...new Set(
    (statement.filteredItems ?? [])
      .map((value) => items.find((item) => item.value === value)?.label),
  )].filter(Boolean);

  return labels.length > 0 ? labels.join(or()) : MISSING;
}

/**
 * @param {object} criterion The filter criterion being edited
 * @param {object} statement The statement built for it
 * @param {{ periodPresetOptions?: object[], clients?: object[], users?: object[] }} [context]
 * @returns {string}
 */
export default function buildFilterLabel(criterion, statement, context = {}) {
  const prefix = getLabelPrefix(criterion);
  const negative = statement?.filterNegative ? `${i18n.t('common.exclude')} ` : '';

  switch (criterion?.filterType) {
    case filterPropertyTypes.Number:
      return describeNumeric(statement, prefix, (value) => value ?? MISSING);

    case filterPropertyTypes.Duration:
      return describeNumeric(
        statement,
        prefix,
        (value) => (typeof value === 'number' ? formatDuration(value) : MISSING),
      );

    case filterPropertyTypes.Boolean: {
      const option = getBooleanOptions().find((item) => item.value === statement.filteredItems);
      /*
       * The prefix carries its own separator when it is derived from the label, and is empty when
       * the criterion asked for none. Only a filterPrefix the criterion set needs one added, since
       * a boolean reads as a sentence and getLabelPrefix leaves it bare.
       */
      const separator = criterion?.filterPrefix ? ': ' : '';

      return `${prefix}${separator}${option?.label ?? MISSING}`;
    }

    case filterPropertyTypes.Date:
      return `${prefix}${negative}${describeDate(statement, context.periodPresetOptions)}`;

    case filterPropertyTypes.Client:
      return `${prefix}${describeSelection(statement.filteredItems, context.clients, 'clientName')}`;

    case filterPropertyTypes.User:
    case filterPropertyTypes.ArrayContainsUser:
      return `${prefix}${describeSelection(statement.filteredItems, context.users, 'name')}`;

    case filterPropertyTypes.StringMatch: {
      // Matches the dialog's own guard: a non-string reads as empty rather than as "null".
      const text = typeof statement.filteredItems === 'string' ? statement.filteredItems : '';
      return `${prefix}${negative}${text}`;
    }

    default:
      return `${prefix}${negative}${describeOptions(statement, criterion)}`;
  }
}
